import { RichMessageContent } from '@/components/content/RichMessageContent';
import { cn } from '@/lib/cn';
import type { SupportMessage } from '@/types/api';

interface MessageBubbleProps {
  message: SupportMessage;
  casePlaintext?: boolean;
  showAutomatedSender?: boolean;
  nativeAttribution?: boolean;
  requestTitle?: string;
  /** Makes the request title a button that opens that request. */
  onOpenRequest?: (id: string) => void;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function nativeFirstName(value: string | null): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (/^(support rep|support|system labs support)$/i.test(normalized)) return null;
  const token = normalized.split(' ', 1)[0];
  if (/^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(token)) return null;
  return token && token.length <= 80 && /^\p{L}[\p{L}'’\-]*$/u.test(token) ? token : null;
}

export function MessageBubble({
  message,
  casePlaintext = false,
  showAutomatedSender = false,
  nativeAttribution = false,
  requestTitle,
  onOpenRequest,
}: MessageBubbleProps) {
  const isPatient = message.sender_role === 'patient';
  const isSystem = message.sender_role === 'system';
  const isHuman = message.sender_role === 'human_agent';
  const repName = nativeAttribution
    ? nativeFirstName(message.sender_name)
    : message.sender_name?.split(' ')[0];
  const title = nativeAttribution && isHuman && message.ticket_id
    && typeof requestTitle === 'string' && requestTitle.trim().length > 0
    && requestTitle.length <= 80 && !/[\r\n\u0000-\u001f]/.test(requestTitle)
    ? requestTitle : null;

  if (isSystem) {
    return (
      <div className="flex justify-center px-15 py-6">
        <div className="text-11 text-ink-muted text-center leading-140 max-w-[260px] min-w-0">
          <RichMessageContent text={message.body} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 px-15', isPatient ? 'items-end' : 'items-start')}>
      {(showAutomatedSender || nativeAttribution) && message.sender_role === 'agent' && (
        <span className="text-10 text-accent font-medium tracking-wide">Sonny</span>
      )}
      {isHuman && (
        <span className="text-10 text-accent font-medium tracking-wide">
          {(repName || 'Support')} · Human support agent
        </span>
      )}
      {title && (onOpenRequest ? (
        <button
          type="button"
          aria-label={`View request: ${title}`}
          onClick={() => message.ticket_id && onOpenRequest(message.ticket_id)}
          className="text-10 text-ink-muted text-left hover:underline underline-offset-2"
        >
          {title}
        </button>
      ) : (
        <span className="text-10 text-ink-muted">{title}</span>
      ))}
      <div
        className={cn(
          'max-w-[80%] min-w-0 overflow-hidden px-12 py-10 text-14 leading-140',
          isPatient && 'bg-black text-white selection:bg-white/30',
          !isPatient && !isHuman && 'bg-sunken text-ink border border-line-subtle',
          isHuman && 'bg-sunken text-ink border border-line-subtle border-l-2 border-l-accent',
        )}
      >
        {casePlaintext && isHuman ? (
          <div className="whitespace-pre-wrap break-words">{message.body}</div>
        ) : (
          // Only Sonny's replies are Markdown. A rep's message shows as typed, like the
          // patient's own: line breaks kept and links clickable (system-support#689).
          <RichMessageContent
            text={message.body}
            markdown={message.sender_role === 'agent'}
            literalImages={isHuman}
            inverse={isPatient}
          />
        )}
      </div>
      <span className="text-10 text-ink-muted">
        {formatRelativeTime(message.created_at)}
      </span>
    </div>
  );
}
